var __runInitializers = (this && this.__runInitializers) || function (thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for (var i = 0; i < initializers.length; i++) {
        value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    }
    return useValue ? value : void 0;
};
var __esDecorate = (this && this.__esDecorate) || function (ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) { if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected"); return f; }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for (var i = decorators.length - 1; i >= 0; i--) {
        var context = {};
        for (var p in contextIn) context[p] = p === "access" ? {} : contextIn[p];
        for (var p in contextIn.access) context.access[p] = contextIn.access[p];
        context.addInitializer = function (f) { if (done) throw new TypeError("Cannot add initializers after decoration has completed"); extraInitializers.push(accept(f || null)); };
        var result = (0, decorators[i])(kind === "accessor" ? { get: descriptor.get, set: descriptor.set } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        }
        else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
};
import { CompiledFunction, DynamicBase, bound, envHasCsp, flatMorph, hasDomain, isArray, printable, throwInternalError, throwParseError } from "@arktype/util";
import { globalConfig, mergeConfigs } from "./config.js";
import { validateUninstantiatedGenericNode } from "./generic.js";
import { nodeImplementationsByKind } from "./kinds.js";
import { RootModule } from "./module.js";
import { parseNode, schemaKindOf } from "./parse.js";
import { normalizeAliasSchema } from "./roots/alias.js";
import { NodeCompiler } from "./shared/compile.js";
import { arkKind, hasArkKind, isNode } from "./shared/utils.js";
export const defaultConfig = Object.assign(flatMorph(nodeImplementationsByKind, (kind, implementation) => [
    kind,
    implementation.defaults
]), {
    jitless: envHasCsp(),
    registerKeywords: false,
    prereducedAliases: false
});
const nonInheritedKeys = [
    "registerKeywords",
    "prereducedAliases"
];
export const extendConfig = (base, extension) => {
    if (!extension)
        return base;
    const result = mergeConfigs(base, extension);
    nonInheritedKeys.forEach(k => {
        if (!(k in extension))
            delete result[k];
    });
    return result;
};
export const resolveConfig = (config) => extendConfig(extendConfig(defaultConfig, globalConfig), config);
const schemaBranchesOf = (schema) => isArray(schema) ? schema
    : "branches" in schema && isArray(schema.branches) ? schema.branches
        : undefined;
const throwMismatchedNodeRootError = (expected, actual) => throwParseError(`Node of kind ${actual} is not valid as a ${expected} definition`);
export const writeDuplicateAliasError = (alias) => `#${alias} duplicates public alias ${alias}`;
const nodeCountsByPrefix = {};
const nodesById = {};
let scopeCount = 0;
const scopesById = {};
let RawRootScope = (() => {
    let _instanceExtraInitializers = [];
    let _schema_decorators;
    let _defineRoot_decorators;
    let _units_decorators;
    return class RawRootScope {
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            _schema_decorators = [bound];
            _defineRoot_decorators = [bound];
            _units_decorators = [bound];
            __esDecorate(this, null, _schema_decorators, { kind: "method", name: "schema", static: false, private: false, access: { has: obj => "schema" in obj, get: obj => obj.schema }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _defineRoot_decorators, { kind: "method", name: "defineRoot", static: false, private: false, access: { has: obj => "defineRoot" in obj, get: obj => obj.defineRoot }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _units_decorators, { kind: "method", name: "units", static: false, private: false, access: { has: obj => "units" in obj, get: obj => obj.units }, metadata: _metadata }, null, _instanceExtraInitializers);
            if (_metadata) Object.defineProperty(this, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        }
        config = __runInitializers(this, _instanceExtraInitializers);
        resolvedConfig;
        id = `$${++scopeCount}`;
        [arkKind] = "scope";
        referencesById = {};
        references = [];
        resolutions = {};
        json = {};
        exportedNames;
        aliases = {};
        resolved = false;
        // these allow builtin types to be accessed during parsing without cyclic imports
        // they are populated as each scope is parsed with `registerKeywords` in its config
        /** @internal */
        static keywords = {};
        /** @internal */
        get keywords() {
            return RawRootScope.keywords;
        }
        static ambient;
        get ambient() {
            return this.constructor.ambient;
        }
        constructor(
        /** The set of names defined at the root-level of the scope mapped to their
         * corresponding definitions.**/
        def, config) {
            this.config = config ?? {};
            this.resolvedConfig = resolveConfig(config);
            this.exportedNames = Object.keys(def).filter(k => {
                if (k[0] === "#") {
                    const name = k.slice(1);
                    if (name in this.aliases)
                        throwParseError(writeDuplicateAliasError(name));
                    this.aliases[name] = def[k];
                    return false;
                }
                if (k in this.aliases)
                    throwParseError(writeDuplicateAliasError(k));
                this.aliases[k] = def[k];
                return true;
            });
            if (this.ambient) {
                // ensure exportedResolutions is populated
                this.ambient.export();
                // TODO: generics and modules
                this.resolutions = flatMorph(this.ambient.resolutions, (alias, resolution) => [
                    alias,
                    hasArkKind(resolution, "root") ?
                        resolution.bindScope(this)
                        : resolution
                ]);
            }
            scopesById[this.id] = this;
        }
        get raw() {
            return this;
        }
        schema(def, opts) {
            return this.node(schemaKindOf(def), def, opts);
        }
        defineRoot(def) {
            return def;
        }
        units(values, opts) {
            const uniqueValues = [];
            for (const value of values)
                if (!uniqueValues.includes(value))
                    uniqueValues.push(value);
            const branches = uniqueValues.map(unit => this.node("unit", { unit }, opts));
            return this.node("union", branches, {
                ...opts,
                prereduced: true
            });
        }
        lazyResolutions = [];
        lazilyResolve(resolve, syntheticAlias) {
            if (!syntheticAlias) {
                nodeCountsByPrefix.synthetic ??= 0;
                syntheticAlias = `synthetic${++nodeCountsByPrefix.synthetic}`;
            }
            const node = this.node("alias", {
                alias: syntheticAlias,
                resolve
            }, { prereduced: true });
            this.lazyResolutions.push(node);
            return node;
        }
        node = (((kinds, nodeSchema, opts) => {
            let kind = typeof kinds === "string" ? kinds : schemaKindOf(nodeSchema, kinds);
            let schema = nodeSchema;
            if (isNode(schema) && schema.kind === kind)
                return schema.bindScope(this);
            if (kind === "alias" && !opts?.prereduced) {
                const resolution = this.resolveRoot(normalizeAliasSchema(schema).alias);
                schema = resolution;
                kind = resolution.kind;
            }
            else if (kind === "union" && hasDomain(schema, "object")) {
                const branches = schemaBranchesOf(schema);
                if (branches?.length === 1) {
                    schema = branches[0];
                    kind = schemaKindOf(schema);
                }
            }
            const impl = nodeImplementationsByKind[kind];
            const normalizedSchema = impl.normalize?.(schema) ?? schema;
            // check again after normalization in case a node is a valid collapsed
            // schema for the kind (e.g. sequence can collapse to element accepting a Node)
            if (isNode(normalizedSchema)) {
                return normalizedSchema.kind === kind ?
                    normalizedSchema.bindScope(this)
                    : throwMismatchedNodeRootError(kind, normalizedSchema.kind);
            }
            const prefix = opts?.alias ?? kind;
            nodeCountsByPrefix[prefix] ??= 0;
            const id = `${prefix}${++nodeCountsByPrefix[prefix]}`;
            const node = parseNode(kind, {
                ...opts,
                id,
                $: this,
                schema: normalizedSchema
            }).bindScope(this);
            nodesById[id] = node;
            if (this.resolved) {
                // this node was not part of the original scope, so compile an anonymous scope
                // including only its references
                if (!this.resolvedConfig.jitless)
                    bindCompiledScope(node.references);
            }
            else {
                // we're still parsing the scope itself, so defer compilation but
                // add the node as a reference
                Object.assign(this.referencesById, node.referencesById);
            }
            return node;
        })).bind(this);
        parseRoot(def, opts) {
            return this.schema(def, opts);
        }
        resolveRoot(name) {
            return (this.maybeResolveRoot(name) ??
                throwParseError(writeUnresolvableMessage(name)));
        }
        maybeResolveRoot(name) {
            const result = this.maybeResolveGenericOrRoot(name);
            if (hasArkKind(result, "generic"))
                return;
            return result;
        }
        maybeResolveGenericOrRoot(name) {
            const resolution = this.maybeResolve(name);
            if (hasArkKind(resolution, "module"))
                return throwParseError(writeMissingSubmoduleAccessMessage(name));
            return resolution;
        }
        preparseRoot(def) {
            return def;
        }
        maybeResolve(name) {
            const resolution = this.maybeShallowResolve(name);
            return typeof resolution === "string" ?
                this.node("alias", { alias: resolution }, { prereduced: true })
                : resolution;
        }
        maybeShallowResolve(name) {
            const cached = this.resolutions[name];
            if (cached)
                return cached;
            let def = this.aliases[name];
            if (!def)
                return this.maybeResolveSubalias(name);
            def = this.preparseRoot(def);
            if (hasArkKind(def, "generic"))
                return (this.resolutions[name] = validateUninstantiatedGenericNode(def));
            if (hasArkKind(def, "module"))
                return (this.resolutions[name] = def);
            this.resolutions[name] = name;
            return (this.resolutions[name] = this.parseRoot(def));
        }
        /** If name is a valid reference to a submodule alias, return its resolution  */
        maybeResolveSubalias(name) {
            return resolveSubalias(this.aliases, name);
        }
        import(...names) {
            return new RootModule(flatMorph(this.export(...names), (alias, value) => [
                `#${alias}`,
                value
            ]));
        }
        _exportedResolutions;
        _exports;
        export(...names) {
            if (!this._exports) {
                this._exports = {};
                for (const name of this.exportedNames)
                    this._exports[name] = this.maybeResolve(name);
                this.lazyResolutions.forEach(node => node.resolution);
                this._exportedResolutions = resolutionsOfModule(this, this._exports);
                // TODO: add generic json
                Object.assign(this.json, flatMorph(this._exportedResolutions, (k, v) => hasArkKind(v, "root") ? [k, v.json] : []));
                Object.assign(this.resolutions, this._exportedResolutions);
                if (this.config.registerKeywords)
                    Object.assign(RawRootScope.keywords, this._exportedResolutions);
                this.references = Object.values(this.referencesById);
                if (!this.resolvedConfig.jitless)
                    bindCompiledScope(this.references);
                this.resolved = true;
            }
            const namesToExport = names.length ? names : this.exportedNames;
            return new RootModule(flatMorph(namesToExport, (_, name) => [
                name,
                this._exports[name]
            ]));
        }
        resolve(name) {
            return this.export()[name];
        }
    };
})();
export { RawRootScope };
const resolveSubalias = (base, name) => {
    const dotIndex = name.indexOf(".");
    if (dotIndex === -1)
        return;
    const dotPrefix = name.slice(0, dotIndex);
    const prefixSchema = base[dotPrefix];
    // if the name includes ".", but the prefix is not an alias, it
    // might be something like a decimal literal, so just fall through to return
    if (prefixSchema === undefined)
        return;
    if (!hasArkKind(prefixSchema, "module"))
        return throwParseError(writeNonSubmoduleDotMessage(dotPrefix));
    const subalias = name.slice(dotIndex + 1);
    const resolution = prefixSchema[subalias];
    // if the first part of name is a submodule but the suffix is
    // unresolvable, we can throw immediately
    if (resolution === undefined) {
        if (hasArkKind(resolution, "module"))
            return resolveSubalias(resolution, subalias);
        return throwParseError(writeUnresolvableMessage(name));
    }
    if (hasArkKind(resolution, "root") || hasArkKind(resolution, "generic"))
        return resolution;
    throwInternalError(`Unexpected resolution for alias '${name}': ${printable(resolution)}`);
};
export const schemaScope = (aliases, config) => new RootScope(aliases, config);
export const RootScope = RawRootScope;
export const root = new RootScope({});
export const schema = root.schema;
export const node = root.node;
export const defineRoot = root.defineRoot;
export const units = root.units;
export const rawRoot = root.raw.schema;
export const rawNode = root.raw.node;
export const defineRawRoot = root.raw.defineRoot;
export const rawUnits = root.raw.units;
export class RawRootModule extends DynamicBase {
}
const resolutionsOfModule = ($, typeSet) => {
    const result = {};
    for (const k in typeSet) {
        const v = typeSet[k];
        if (hasArkKind(v, "module")) {
            const innerResolutions = resolutionsOfModule($, v);
            const prefixedResolutions = flatMorph(innerResolutions, (innerK, innerV) => [`${k}.${innerK}`, innerV]);
            Object.assign(result, prefixedResolutions);
        }
        else if (hasArkKind(v, "generic"))
            result[k] = v;
        else if (hasArkKind(v, "root"))
            result[k] = v;
        else
            throwInternalError(`Unexpected scope resolution ${printable(v)}`);
    }
    return result;
};
export const writeUnresolvableMessage = (token) => `'${token}' is unresolvable`;
export const writeNonSubmoduleDotMessage = (name) => `'${name}' must reference a module to be accessed using dot syntax`;
export const writeMissingSubmoduleAccessMessage = (name) => `Reference to submodule '${name}' must specify an alias`;
export const bindCompiledScope = (references) => {
    const compiledTraversals = compileScope(references);
    for (const node of references) {
        if (node.jit) {
            // if node has already been bound to another scope or anonymous type, don't rebind it
            continue;
        }
        node.jit = true;
        node.traverseAllows =
            compiledTraversals[`${node.id}Allows`].bind(compiledTraversals);
        if (node.isRoot() && !node.allowsRequiresContext) {
            // if the reference doesn't require context, we can assign over
            // it directly to avoid having to initialize it
            node.allows = node.traverseAllows;
        }
        node.traverseApply =
            compiledTraversals[`${node.id}Apply`].bind(compiledTraversals);
    }
};
const compileScope = (references) => new CompiledFunction()
    .block("return", js => {
    references.forEach(node => {
        const allowsCompiler = new NodeCompiler("Allows").indent();
        node.compile(allowsCompiler);
        const applyCompiler = new NodeCompiler("Apply").indent();
        node.compile(applyCompiler);
        js.line(`${allowsCompiler.writeMethod(`${node.id}Allows`)},`).line(`${applyCompiler.writeMethod(`${node.id}Apply`)},`);
    });
    return js;
})
    .compile()();
